<template>
  <v-dialog v-model="showPrivacyPolicyFormDialog" :max-width="maxWidth">
    <v-card>
      <v-card-title>
        <span class="headline">{{ $t("pages.privacyPolicy.form.title") }}</span>
      </v-card-title>
      <v-card-text>
        <v-container class="pa-0">
          <v-row no-gutters>
            <v-container class="white">
              <span class="input-label">{{ $t('pages.privacyPolicy.form.language') }}</span>
              <validated-component
                name="Language"
                v-model="privacyPolicy.language"
                :validator="$v.privacyPolicy.language"
              >
                <v-select
                  class="field mt-4"
                  outlined
                  hide-details
                  no-filter
                  :items="availableLanguages"
                  v-model="privacyPolicy.language"
                  prepend-inner-icon="mdi-translate"
                  append-icon="$icons_system_caret_down"
                ></v-select>
              </validated-component>

              <span class="input-label">{{ $t('pages.privacyPolicy.form.type') }}</span>
              <validated-component
                name="Type"
                v-model="privacyPolicy.type"
                :validator="$v.privacyPolicy.type"
              >
                <v-select
                  class="field mt-4"
                  outlined
                  hide-details
                  no-filter
                  :items="privacyPolicyTypes"
                  v-model="privacyPolicy.type"
                  append-icon="$icons_system_caret_down"
                ></v-select>
              </validated-component>

              <template v-if="privacyPolicy.type === 'FILE_UPLOAD'">
                <validated-component
                  name="File"
                  v-model="privacyPolicy.file"
                  :validator="$v.privacyPolicy.file"
                >
                  <v-file-input
                    v-model="privacyPolicy.file"
                    :label="$t('pages.privacyPolicy.form.file')"
                    :placeholder="$t('pages.privacyPolicy.form.file')"
                    prepend-inner-icon="mdi-upload"
                    prepend-icon=""
                    hide-details
                    outlined>
                  </v-file-input>
                </validated-component>
              </template>
              <template v-else-if="privacyPolicy.type === 'FLEET_URL'">
                <validated-component
                  name="Url"
                  v-model="privacyPolicy.url"
                  :validator="$v.privacyPolicy.url"
                >
                  <v-text-field
                    v-model="privacyPolicy.url"
                    :label="$t('pages.privacyPolicy.form.url')"
                    :placeholder="$t('pages.privacyPolicy.form.url')"
                    prepend-inner-icon="mdi-link"
                    hide-details
                    outlined/>
                </validated-component>
              </template>

              <div role="alert" :if="error">{{error}}</div>

                <v-alert
                  v-if="uploadingWordFile"
                  outlined
                  dense
                  prominent
                  border="left"
                  type="warning"
                  color="warning">
                  {{ $t('pages.privacyPolicy.form.uploadingWordFile') }}
                </v-alert>

            </v-container>
          </v-row>
        </v-container>
      </v-card-text>

    <v-card-actions >
        <v-spacer></v-spacer>
        <v-btn color="primary" outlined @click="hidePrivacyPolicyFormDialog">{{
            $t("pages.privacyPolicy.form.cancel")
          }}</v-btn>
        <v-btn color="primary" @click="savePrivacyPolicy()" :loading="saveLoading" :disabled="disableSubmit">{{
            $t("pages.privacyPolicy.form.save")
          }}</v-btn>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import * as _ from 'lodash';
import { required } from 'vuelidate/lib/validators';

export default {
  props: {
    availableLanguages: {
      type: Array,
      required: true,
    },
    showPrivacyPolicyFormDialog: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    privacyPolicy: {
      language: null,
      type: null,
      url: null,
      file: null,
    },
    privacyPolicyTypes: [
      { text: 'File Upload', value: 'FILE_UPLOAD' },
      { text: 'URL', value: 'FLEET_URL' },
    ],
    saveLoading: false,
    error: null,
  }),
  methods: {
    resetForm() {
      this.privacyPolicy = {
        language: null,
        type: null,
        url: null,
        file: null,
      };

      this.error = null;

      this.$nextTick(() => { this.$v.$reset(); });
    },
    hidePrivacyPolicyFormDialog() {
      this.$emit('hidePrivacyPolicyFormDialog');
      this.resetForm();
    },
    async savePrivacyPolicy() {
      try {
        this.saveLoading = true;
        const { language, file, type, url } = this.privacyPolicy;

        const payload = _.omitBy({ language, type, file, url }, _.isNil);

        const formData = new FormData();
        Object.keys(payload).forEach((key) => {
          formData.append(key, payload[key]);
        });

        const result = await this.$store.dispatch('bookingChannel/createBookingChannelPrivacyPolicy', {
          bookingChannelId: window.bookingChannelConfig.id,
          payload: formData,
        });

        if (!result.errorCode) {
          this.$emit('savePrivacyPolicy', result);
          this.resetForm();
        }
      } finally {
        this.saveLoading = false;
      }
    },
  },
  computed: {
    maxWidth() {
      if (this.isMobile) {
        return '100%';
      }
      return '1200px';
    },
    disableSubmit() {
      return this.$v.$invalid;
    },
    uploadingWordFile() {
      return this.privacyPolicy.file && this.privacyPolicy.file.name.endsWith('.docx');
    },
  },
  validations() {
    return {
      privacyPolicy: {
        language: {
          required,
        },
        type: {
          required,
        },
        file: {
          required: (value) => {
            if (this.privacyPolicy.type === 'FILE_UPLOAD') {
              return required(value);
            }
            return true;
          },
          size: (value) => {
            if (value) {
              if (this.privacyPolicy.type === 'FILE_UPLOAD') {
                return value.size < 5000000; // 5MB
              }
            }

            return true;
          },
        },
        url: {
          required: (value) => {
            if (this.privacyPolicy.type === 'FLEET_URL') {
              return required(value);
            }
            return true;
          },
        },
      },
    };
  },
};
</script>
